/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { isEmpty } from 'lodash'

import styles from './Main.module.scss'

import ConcessionsCartSidebar from '../containers/ConcessionsCartSidebar'
import CartModal from '../containers/CartModal'
import Items from '../components/Items'
import Navbar from '../containers/Navbar'
import CategoryBar from '../containers/CategoryBar'

import HeaderBar from '../components/HeaderBar'

import { createOrderParams } from '../utils/orderUtils'
import { incrementNextOrderNumber } from '../utils/orderNumbers'

import { getPerformSafeUpdate } from '../selectors/appState'
import { getOrderInProgress, shouldDisplayOfflineTransactionHeader } from '../selectors/order'
import { getActiveAttendantId, getAttendants } from '../selectors/attendant'
import { clearRemoteOrderTotal } from '../actions/orderTotalRemote'
import { clearOrderInProgress, resetMutationInProgress } from '../actions/order'
import { refreshCache } from '../utils/newAppVersionAvailable'
import { STADIUM_ORDER_STATES } from '../utils/orderStates'
import useMenu from '../context/hooks/useMenu'
import { RESOURCE_RESTRAINED_HARDWARE } from '../VNMode/Utils'
import { bridge_getDeviceModel } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

const Main = ({ className }) => {
  const dispatch = useDispatch()
  const { replace } = useHistory()
  const attendantsExist = !isEmpty(useSelector(getAttendants))
  const hasActiveAttendant = useSelector(getActiveAttendantId) !== undefined
  const performSafeUpdate = useSelector(getPerformSafeUpdate)

  const classNames = cn(styles.main, className)
  const orderInProgress = useSelector((state) => getOrderInProgress(state))
  const menuContainerRef = useRef()
  const { handleScroll, isMobile } = useMenu()
  const isResourceRestrained = useMemo(() => RESOURCE_RESTRAINED_HARDWARE?.includes(bridge_getDeviceModel()), []) // TODO: [T38895] remove this when category navigation is redone
  const isTabbed =
    orderInProgress?.state === STADIUM_ORDER_STATES.TABBED ||
    createOrderParams(orderInProgress)?.state === STADIUM_ORDER_STATES.TABBED ||
    orderInProgress?.state === STADIUM_ORDER_STATES.TABBED_OFFLINE ||
    createOrderParams(orderInProgress)?.state === STADIUM_ORDER_STATES.TABBED_OFFLINE

  const urlParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    /**
    TODO: T37204 find a way to increment order numbers without skipping them, this will skip an order
    number every time the user returns from any screen to the menu even if an order was not created.
    We should investigate and find a way to increment the order number only when an order is created and mitigate
    possible order number skipping.
    */

    //when we get the payment url param === card that means that we've returned from the tender selection in CFD mode.
    //In this scenario we don't need to increment the order number
    if (!isTabbed && urlParams.get('payment') !== 'card') {
      incrementNextOrderNumber()
    }
  }, [])

  useEffect(() => {
    dispatch(resetMutationInProgress())
  }, [resetMutationInProgress])

  useEffect(() => {
    if (performSafeUpdate && isEmpty(orderInProgress)) {
      refreshCache()
    }
  }, [performSafeUpdate, orderInProgress])

  /**
   * first thing we need to do is prevent the main component to clear order in progress and total
   * for tabbed orders
   *
   * We can't use the isTabbed util here because it uses orderInProgress obj, if we add
   * orderInProgress to the dependency array we enter in a re render loop.
   * This way we follow the hooks rules and prevent endless renders loops
   */
  useEffect(() => {
    if (isTabbed) {
      return
    }

    dispatch(clearRemoteOrderTotal())

    // don't call clear if there's nothing to clear
    if (!orderInProgress?.state) return

    dispatch(clearOrderInProgress())
  }, [orderInProgress?.state])

  useEffect(() => {
    if (attendantsExist && !hasActiveAttendant) {
      replace('/user-login')
    }
  }, [hasActiveAttendant])

  const shouldDisplayHeader = useSelector(shouldDisplayOfflineTransactionHeader)
  const cart =
    document?.body?.clientWidth <= 800 ? (
      <CartModal />
    ) : (
      <ConcessionsCartSidebar className={cn(shouldDisplayHeader ? styles.shortenSidebar : styles.sidebar)} />
    )

  const handleMenuScroll = useCallback(() => {
    if (isMobile) return
    if (isResourceRestrained) return

    handleScroll(menuContainerRef)
  }, [handleScroll, isMobile, isResourceRestrained])

  return (
    <div className={classNames}>
      <HeaderBar />
      <Navbar className={styles.navbar} />
      {cart}
      <div
        className={shouldDisplayHeader ? styles.contentWithHeaderBar : styles.content}
        ref={menuContainerRef}
        onScroll={handleMenuScroll}
      >
        <CategoryBar className={styles.categoryHeaderContainer} isResourceRestrained={isResourceRestrained} />
        <Items className={styles.items} isResourceRestrained={isResourceRestrained} />
        <div className={styles.bottomGradient}></div>
      </div>
    </div>
  )
}

export default memo(Main)
